'use client';

import React from 'react';
import { Box, Typography, Alert, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CalendarConnectionStatus } from './types';

interface CalendarConnectionStatusProps {
    status: CalendarConnectionStatus;
    title?: string;
    description?: string;
    onContinue?: () => void;
    onReconfigure?: () => void;
    variant?: 'onboarding' | 'settings' | 'admin';
}

const CalendarConnectionStatusComponent: React.FC<CalendarConnectionStatusProps> = ({
    status,
    title = "Connect Your Calendar",
    description = "Your venue's calendar is now connected. We'll automatically sync your venue's availability and prevent double bookings. You can manage your calendar settings in the Venue Admin dashboard.",
    onContinue,
    onReconfigure,
    variant = 'onboarding'
}) => {
    if (!status.isConnected) {
        return null;
    }

    const getSuccessMessage = () => {
        const count = status.connectedCalendars.length;
        if (count === 0) return "Your calendar has been successfully connected to Litty!";
        if (count === 1) return `Your calendar "${status.connectedCalendars[0]}" has been successfully connected to Litty!`;
        return `Your calendars (${status.connectedCalendars.join(', ')}) have been successfully connected to Litty!`;
    };

    const getContinueButtonText = () => {
        switch (variant) {
            case 'onboarding':
                return 'Continue to next step';
            case 'settings':
                return 'Close';
            case 'admin':
                return 'Done';
            default:
                return 'Continue';
        }
    };

    return (
        <Box className="py-6">
            <Typography variant="h5" gutterBottom fontFamily="Roboto Condensed" className="text-[#C0C0DD] mb-2">
                {title}
            </Typography>

            <Alert
                icon={<CheckCircleIcon fontSize="inherit" />}
                severity="success"
                sx={{
                    mb: 4,
                    mt: 4,
                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                    color: '#4CAF50',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    borderRadius: 2,
                }}
            >
                {getSuccessMessage()}
            </Alert>

            <Typography variant="body1" paragraph className="text-gray-300 mb-6">
                {description}
            </Typography>

            {status.connectedCalendars.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" className="text-gray-400 mb-2">
                        Connected calendars:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {status.connectedCalendars.map((calendar, index) => (
                            <Box
                                key={index}
                                sx={{
                                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                                    color: '#4CAF50',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 1,
                                    border: '1px solid rgba(76, 175, 80, 0.3)',
                                    fontSize: '0.875rem'
                                }}
                            >
                                {calendar}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6 }}>
                {onReconfigure && (
                    <Button
                        variant="outlined"
                        onClick={onReconfigure}
                        sx={{
                            color: '#C0C0DD',
                            borderColor: '#C0C0DD',
                            '&:hover': {
                                borderColor: '#E0B1F1',
                                backgroundColor: 'rgba(224, 177, 241, 0.1)'
                            }
                        }}
                    >
                        Reconfigure
                    </Button>
                )}

                {onContinue && (
                    <Button
                        variant="contained"
                        onClick={onContinue}
                        sx={{
                            bgcolor: '#C0C0DD',
                            color: '#1C1C34',
                            fontWeight: 600,
                            py: 1.5,
                            px: 4,
                            ml: 'auto',
                            '&:hover': {
                                bgcolor: '#E0B1F1',
                            },
                        }}
                    >
                        {getContinueButtonText()}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default CalendarConnectionStatusComponent; 